import { Button, Input } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import Navbar from "../../components/landing/modern/Navbar";
import ModernFooter from "../../components/landing/modern/ModernFooter";
import SEOHead from "../../components/tools/SEOHead";
import { buildAppLink } from "../../utils/landingRedirect";

interface Tool {
  name: string;
  description: string;
  href: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  category: string;
  popular?: boolean;
}

const tools: Tool[] = [
  {
    name: "YouTube Summarizer",
    description: "Transform long YouTube videos into concise summaries with AI-generated timestamps and key points.",
    href: "/tools/youtube-summarizer",
    icon: "lucide:youtube",
    iconColor: "text-red-500",
    bgColor: "bg-red-50",
    category: "Summarization",
    popular: true,
  },
  {
    name: "PDF Summarizer",
    description: "Turn lengthy PDFs, research papers, and documents into clear, concise summaries instantly.",
    href: "/tools/pdf-summarizer",
    icon: "lucide:file-text",
    iconColor: "text-orange-500",
    bgColor: "bg-orange-50",
    category: "Summarization",
    popular: true,
  },
  {
    name: "AI Audio Transcriber",
    description: "Convert any audio file or voice recording to accurate text with speaker detection.",
    href: "/tools/audio-transcriber",
    icon: "lucide:mic",
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50",
    category: "Transcription",
    popular: true,
  },
  {
    name: "AI Flashcard Maker",
    description: "Generate study flashcards automatically from any text, PDF, or notes with AI.",
    href: "/tools/flashcard-maker",
    icon: "lucide:layers",
    iconColor: "text-purple-500",
    bgColor: "bg-purple-50",
    category: "Study Tools",
  },
  {
    name: "AI Quiz Generator",
    description: "Create practice quizzes with multiple choice, true/false, and short answer questions.",
    href: "/tools/quiz-generator",
    icon: "lucide:help-circle",
    iconColor: "text-green-500",
    bgColor: "bg-green-50",
    category: "Study Tools",
  },
  {
    name: "Meeting Notes Generator",
    description: "Convert meeting recordings into organized notes with action items and decisions.",
    href: "/tools/meeting-notes",
    icon: "lucide:users",
    iconColor: "text-indigo-500",
    bgColor: "bg-indigo-50",
    category: "Productivity",
  },
  {
    name: "Lecture Notes AI",
    description: "Transform lecture recordings and videos into comprehensive study notes.",
    href: "/tools/lecture-notes",
    icon: "lucide:graduation-cap",
    iconColor: "text-emerald-500",
    bgColor: "bg-emerald-50",
    category: "Study Tools",
  },
];

const categories = ["All", "Summarization", "Transcription", "Study Tools", "Productivity"];

const AllTools: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filteredTools, setFilteredTools] = useState(tools);

  useEffect(() => {
    let result = tools;

    // Filter by category
    if (selectedCategory !== "All") {
      result = result.filter((tool) => tool.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (tool) =>
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query)
      );
    }

    setFilteredTools(result);
  }, [searchQuery, selectedCategory]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Free AI Tools - YouWo AI",
    "description": "Collection of free AI-powered productivity tools including video summarizers, transcribers, flashcard makers, and more.",
    "hasPart": tools.map((tool) => ({
      "@type": "WebApplication",
      "name": tool.name,
      "description": tool.description,
      "url": `https://youwo.ai${tool.href}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Free AI Tools - Video Summarizer, PDF Summarizer & More | YouWo AI"
        description="Explore our collection of free AI-powered tools. YouTube summarizer, PDF summarizer, audio transcriber, flashcard maker, quiz generator, and more. No sign-up required."
        keywords="free ai tools, youtube summarizer, pdf summarizer, audio transcriber, flashcard maker, quiz generator, ai productivity tools"
        canonicalUrl="https://youwo.ai/tools"
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Free AI Tools
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
              Powerful AI tools to boost your productivity. Summarize videos, transcribe audio,
              create flashcards, and more — all for free.
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools..."
                size="lg"
                startContent={
                  <Icon icon="lucide:search" className="text-gray-400 text-xl" />
                }
                classNames={{
                  input: "text-base",
                  inputWrapper: "h-14 border-gray-200 hover:border-cornflowerblue focus-within:border-cornflowerblue shadow-sm",
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 border-b border-gray-100 sticky top-16 bg-white/95 backdrop-blur z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-cornflowerblue text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Tools */}
      {selectedCategory === "All" && !searchQuery && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Icon icon="lucide:star" className="text-yellow-500" />
                Most Popular
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {tools
                .filter((tool) => tool.popular)
                .map((tool, index) => (
                  <motion.a
                    key={tool.href}
                    href={tool.href}
                    className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-cornflowerblue/30 hover:shadow-xl transition-all duration-300 no-underline"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className={`w-14 h-14 rounded-2xl ${tool.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon icon={tool.icon} className={`text-2xl ${tool.iconColor}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-cornflowerblue transition-colors">
                      {tool.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {tool.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-cornflowerblue text-sm font-medium group-hover:gap-2 transition-all">
                      Try for free
                      <Icon icon="lucide:arrow-right" />
                    </span>
                  </motion.a>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* All Tools Grid */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              {selectedCategory === "All" ? "All Tools" : selectedCategory}
            </h2>
            <p className="text-gray-600 mt-2">
              {filteredTools.length} tool{filteredTools.length !== 1 ? "s" : ""} available
            </p>
          </motion.div>

          {filteredTools.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTools.map((tool, index) => (
                <motion.a
                  key={tool.href}
                  href={tool.href}
                  className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-cornflowerblue/30 hover:shadow-xl transition-all duration-300 no-underline"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${tool.bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon icon={tool.icon} className={`text-xl ${tool.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-cornflowerblue transition-colors">
                          {tool.name}
                        </h3>
                        {tool.popular && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mb-2 block">{tool.category}</span>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {tool.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-cornflowerblue text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                        Try now
                        <Icon icon="lucide:arrow-right" />
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon icon="lucide:search-x" className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No tools found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-cornflowerblue to-blue-600">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Supercharge Your Productivity?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Sign up for a free account to save your work, access premium features, and use all tools unlimited.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                as="a"
                href={buildAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                className="bg-white text-cornflowerblue font-semibold px-8 py-6 hover:bg-gray-50 no-underline"
              >
                Get Started Free
                <Icon icon="lucide:arrow-right" className="ml-2" />
              </Button>
              <Button
                as="a"
                href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
                target="_blank"
                rel="noopener noreferrer"
                variant="bordered"
                size="lg"
                className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 no-underline"
                startContent={<Icon icon="logos:apple" className="text-xl" />}
              >
                Download iOS
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
};

export default AllTools;
